import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Stack } from '@mui/material';

import { TextField } from '../../../../../../../shared/components/form';
import { useBeforeClosePopup } from '../../../../../../../shared/components/popup/hooks/useBeforeClosePopup';
import { PopupService } from '../../../../../../../shared/components/popup/services/popup.service';
import { CustomerCRMNote } from '../../../../../services/customer.service';
import { IAddOrEditNote } from './addOrEditNote.popup';

// Note is nvarchar(8000) in fkCustomerCRMNotes; the backend rejects anything longer.
export const NOTE_MAX_LENGTH = 8000;

export const Content: FC<IContent> = ({ stream }) => {
  const { t } = useTranslation();
  const [note, setNote] = useState<CustomerCRMNote | undefined>();
  const [text, setText] = useState<string>('');

  useEffect(() => {
    const openSub = stream.actionListener('open').subscribe((data) => {
      setNote(data?.values?.note);
      setText(data?.values?.note?.note ?? '');
    });
    return () => {
      openSub.unsubscribe();
    };
  }, []);

  const initialText = note?.note ?? '';
  // Windows validates the memo with CheckNotEmpty, so a blank note cannot be saved.
  const canSave = !!text.trim() && text !== initialText;

  const saveData = () => {
    if (!canSave) {
      return;
    }
    stream.emit('save', {
      values: { note: { ...(note as CustomerCRMNote), note: text } },
    });
    stream.emit('close');
  };

  useBeforeClosePopup({
    stream,
    isDirty: text !== initialText,
    deps: [initialText],
  });

  return (
    <Stack sx={{ width: '594px', p: 3 }}>
      <TextField
        label={t('common.note')}
        value={text}
        onChange={(e) => setText(e.target.value)}
        slotProps={{ htmlInput: { maxLength: NOTE_MAX_LENGTH } }}
        width='100%'
        multiline
        rows={6}
        autoFocus
      />
      <Stack direction='row' sx={{ justifyContent: 'flex-end', mt: 3 }}>
        <Button
          onClick={saveData}
          disabled={!canSave}
          variant='contained'
          sx={{ minWidth: '125px' }}
        >
          {t('common.save')}
        </Button>
      </Stack>
    </Stack>
  );
};

export interface IContent {
  stream: PopupService<IAddOrEditNote>;
}
